/**
 * Storage keys for localStorage and sessionStorage
 *
 * This file centralizes all storage keys used across the application
 * to prevent duplication and ensure uniqueness.
 */

const APP_PREFIX = 'mrge';

export const STORAGE_KEYS = {
  /** Persisted React Query cache. Only mirrors server data, so safe storage drops it when localStorage is full. */
  QUERY_CACHE: 'mrge-query-cache',

  CODE_WORKSPACE_PREFERENCES: `${APP_PREFIX}:code-workspace-preferences`,

  CODE_SESSION_SEEN_ACTIVITY: `${APP_PREFIX}:code-session-seen-activity`,

  CODE_SESSION_PINS: `${APP_PREFIX}:code-session-pins`,

  /** Which install prompts (desktop app, Slack) this browser has shown, dismissed and counted. */
  INSTALL_PROMPTS: `${APP_PREFIX}:install-prompts`,

  /** sessionStorage: a SAML SSO "Sign in again" click, read when GitHub returns the user. */
  SAML_SSO_SIGN_IN_ATTEMPT: `${APP_PREFIX}:saml-sso-sign-in-attempt`,

  CODE_LAST_REPO: `${APP_PREFIX}:code-last-repo`,

  CODE_LAST_MODEL: `${APP_PREFIX}:code-last-model`,

  /** The effort last picked for a new /code session, reused where the next model offers it. */
  CODE_LAST_REASONING_EFFORT: `${APP_PREFIX}:code-last-reasoning-effort`,

  /** The speed last picked for a new /code session, reused where the next model offers it. */
  CODE_LAST_SPEED: `${APP_PREFIX}:code-last-speed`,

  /** Where the /code landing runs the next session, Cloud or This computer, and the folder picked on this computer. */
  CODE_LAST_RUN: `${APP_PREFIX}:code-last-run`,

  /**
   * Folders the /code landing took on this computer, most recent first, which its folder picker
   * lists. Not a field on `CODE_LAST_RUN`: desktop windows run old bundles for days and write that
   * key whole, which would drop a field they don't know.
   */
  CODE_RECENT_FOLDERS: `${APP_PREFIX}:code-recent-folders`,

  /** The approval mode the /code landing last picked for this computer, kept apart from `CODE_LAST_RUN` as the folders are. */
  CODE_APPROVAL_MODE: `${APP_PREFIX}:code-approval-mode`,

  /** Whether this browser has answered the composer's one-time offer to set up a repository's environment. */
  CODE_SET_UP_ENVIRONMENT_ANSWERED: `${APP_PREFIX}:code-set-up-environment-answered`,

  CODE_FILE_COMMENTS: (userId: string, sessionId: string) =>
    `${APP_PREFIX}:code-file-comments:${JSON.stringify([userId, sessionId])}`,

  /** sessionStorage: a /code composer's unsent text, per person and session (`new` for the composer that starts one). */
  CODE_COMPOSER_DRAFT: (userId: string, composer: string) =>
    `${APP_PREFIX}:code-composer-draft:${JSON.stringify([userId, composer])}`,

  /** sessionStorage: a pull request page's untouched chat, handed to the page's next load in the tab (`page-chat-handoff.ts`). */
  PR_PAGE_CHAT_HANDOFF: (pullRequestId: string) =>
    `${APP_PREFIX}:pr-page-chat-handoff:${pullRequestId}`,

  COMMENT_DRAFT: (owner: string, repo: string, pullNumber: string) =>
    `${APP_PREFIX}:comment-draft-${owner}-${repo}-${pullNumber}`,

  COMMENT_REPLY_DRAFT: (owner: string, repo: string, pullNumber: string, parentId: string) =>
    `${APP_PREFIX}:comment-reply-draft-${owner}-${repo}-${pullNumber}-${parentId}`,

  FILE_COMMENT_DRAFT: (owner: string, repo: string, pullNumber: string, filename: string) =>
    `${APP_PREFIX}:file-comment-draft-${owner}-${repo}-${pullNumber}-${filename}`,

  PR_REVIEW_COMMENT: (owner: string, repo: string, pullNumber: string) =>
    `${APP_PREFIX}:pr-review-comment-${owner}-${repo}-${pullNumber}`,

  /** Whether this browser shows pull requests' demo cards collapsed, as the viewer last left one. */
  PR_DEMO_COLLAPSED: `${APP_PREFIX}:pr-demo-collapsed`,

  INBOX_VISIT_COUNT: 'inbox-visit-count',

  /** Set once this user ends the inbox's first-visit tour in this browser. */
  INBOX_TOUR_V1: (userId: string) => `inbox-tour-v1:${userId}`,

  /** Set once this user saw auto-fix's introduction after turning it on, in this browser. */
  AUTO_FIX_PROMO_V1: (userId: string) => `${APP_PREFIX}:auto-fix-promo-v1:${userId}`,

  /**
   * Set once this user chose "Not now" on the fix session's auto-fix card for this pull request
   * (`owner/repo#number`), in this browser. Its asks stay hidden there; other pull requests ask
   * again.
   */
  AUTO_FIX_CARD_ASKS_DISMISSED_V1: (userId: string, pullRequest: string) =>
    `${APP_PREFIX}:auto-fix-card-asks-dismissed-v1:${userId}:${pullRequest.toLowerCase()}`,

  SLACK_PROMOTION_TOAST_V1_VISIT_COUNT: `${APP_PREFIX}:slack-promotion-toast-v1-visit-count`,

  SLACK_PROMOTION_TOAST_V1_SHOWN: `${APP_PREFIX}:slack-promotion-toast-v1-shown`,

  ONBOARDING_TODOS: (installationId: string) => `${APP_PREFIX}:onboarding-todos-${installationId}`,

  /** A dismissed Members suggestion to review a bot's PRs, per organization and bot. */
  BOT_REVIEW_SUGGESTION_DISMISSED: (installationId: number, botLogin: string) =>
    `${APP_PREFIX}:bot-review-suggestion-dismissed:${installationId}:${botLogin.toLowerCase()}`,

  /** A skipped Auto-fix tab offer to set up a repository's test environment, per repository. */
  AUTO_FIX_ENVIRONMENT_SETUP_SKIPPED: (installationId: number, repositoryId: number) =>
    `${APP_PREFIX}:auto-fix-environment-setup-skipped:${installationId}:${repositoryId}`,

  /** Super admin pages this browser opened, and on how many days, to order the dashboard's links. */
  SUPER_ADMIN_PAGE_VISITS: `${APP_PREFIX}:super-admin-page-visits`,

  /** A public catalog agent a visitor chose to add, kept through sign-up and GitHub app install. */
  PENDING_CATALOG_AGENT: `${APP_PREFIX}:pending-catalog-agent`,

  /** The MCP client this browser last picked on the MCP server setup page. */
  MCP_SETUP_CLIENT: `${APP_PREFIX}:mcp-setup-client`,

  /**
   * Pull requests a user opened in this browser, newest first, which the command palette lists.
   * Keyed by user, so another account signed in to the same browser never sees them.
   */
  RECENT_PULL_REQUESTS: (userId: string) => `${APP_PREFIX}:recent-pull-requests:${userId}`,

  // Lowercased because GitHub treats owner/repo case-insensitively while route
  // params carry whatever casing the incoming link used.
  MERGE_METHOD_PREFERENCE: (owner: string, repo: string) =>
    `${APP_PREFIX}:merge-method-${owner.toLowerCase()}-${repo.toLowerCase()}`,
} as const;
